/** packages/shared-components/src/hooks/useAdvancedKey.ts */

import { useState, useCallback, useRef, useEffect } from "react";
import {
  CommonKeyboardType,
  HigherKeyMode, // 值导入
  type DKSData,
  type MPTData,
  type MTData,
  type TGLData,
  type ENDData,
  type SOCDData,
  type RSData,
  type SnapKeyData,
  type MDSData,
  type CBData,
} from "@eweadn-qiankun/shared-types";
import type { KeyboardType, KeyboardSDK } from "../components/Keyboard/types";
import { isHuafenda24GKeyboard } from "@eweadn-qiankun/shared-utils";
import { isDeviceSessionStandbyRuntime } from "../utils/remoteDeviceConfigEvents";
import { OWNER_RESYNC_LOADING_CHANGED_EVENT } from "../utils/deviceSessionOwnerResync";
// 高级键配置联合类型
export type AdvancedKeyConfig =
  | { mode: HigherKeyMode.NONE }
  | { mode: HigherKeyMode.DKS; data: DKSData }
  | { mode: HigherKeyMode.MPT; data: MPTData }
  | { mode: HigherKeyMode.MT; data: MTData }
  | { mode: HigherKeyMode.TGL; data: TGLData }
  | { mode: HigherKeyMode.END; data: ENDData }
  | { mode: HigherKeyMode.SOCD; data: SOCDData }
  | { mode: HigherKeyMode.RS; data: RSData }
  | { mode: HigherKeyMode.SNAPKEY; data: SnapKeyData }
  | { mode: HigherKeyMode.MDS; data: MDSData }
  | { mode: HigherKeyMode.CB; data: CBData };

export interface UseAdvancedKeyOptions {
  sdkApi?: KeyboardSDK;
  keyboardType: KeyboardType;
  autoLoad?: boolean;
  /** 用于融园等方案跳过星闪式全表扫描 */
  solutionProvider?: string;
}

export interface UseAdvancedKeyResult {
  advancedKeyCache: Record<string, AdvancedKeyConfig>;
  isLoading: boolean;
  isModifying: boolean;
  error: Error | null;

  getHigherKey: (row: number, col: number) => Promise<AdvancedKeyConfig | null>;
  deleteHigherKey: (row: number, col: number) => Promise<boolean>;

  setDKS: (row: number, col: number, data: DKSData) => Promise<boolean>;
  setMPT: (row: number, col: number, data: MPTData) => Promise<boolean>;
  setMT: (row: number, col: number, data: MTData) => Promise<boolean>;
  setTGL: (row: number, col: number, data: TGLData) => Promise<boolean>;
  setEND: (row: number, col: number, data: ENDData) => Promise<boolean>;
  setSOCD: (
    row: number,
    col: number,
    row2: number,
    col2: number,
    socdMode: number,
    delay: number,
    kcs: number[]
  ) => Promise<boolean>;
  setRS: (row: number, col: number, data: RSData) => Promise<boolean>;
  setSnapKey?: (
    row: number,
    col: number,
    data: SnapKeyData
  ) => Promise<boolean>;
  setMDS?: (row: number, col: number, data: MDSData) => Promise<boolean>;
  setCB: (row: number, col: number, data: CBData) => Promise<boolean>;

  loadAllAdvancedKeys: (options?: {
    force?: boolean;
  }) => Promise<Record<string, AdvancedKeyConfig>>;
  replaceAdvancedKeyCache: (cache: Record<string, AdvancedKeyConfig>) => void;
  clearCache: () => void;
}

interface KeyboardMatrixSize {
  rows: number;
  cols: number;
}

const KEYBOARD_MATRIX_SIZES: Record<CommonKeyboardType, KeyboardMatrixSize> = {
  "63": { rows: 6, cols: 14 },
  "68": { rows: 6, cols: 15 },
  "68EVO": { rows: 6, cols: 15 },
  "84": { rows: 6, cols: 15 },
  deep80max: { rows: 6, cols: 15 },
  "87": { rows: 6, cols: 17 },
  es87: { rows: 6, cols: 21 },
  huafenda87: { rows: 6, cols: 17 },
  rongyuan87: { rows: 6, cols: 17 },
  huafenda68: { rows: 6, cols: 15 },
  tank68he: { rows: 6, cols: 15 },
  "99": { rows: 6, cols: 17 },
  "98": { rows: 6, cols: 19 },
  X98_1K: { rows: 6, cols: 21 },
  m80: { rows: 6, cols: 17 },
  m71: { rows: 6, cols: 19 },
  t87: { rows: 6, cols: 17 },
  m75: { rows: 6, cols: 17 },
  "104": { rows: 6, cols: 21 },
  keymap104: { rows: 6, cols: 21 },
  "75Pro": { rows: 6, cols: 15 },
  x75v2: { rows: 6, cols: 17 },
  x75gc: { rows: 6, cols: 17 },
  x75v3: { rows: 6, cols: 16 },
  /** 与 x75v3 同构 75% 紧凑配列（6 行 × 16 列有效矩阵） */
  gamma75: { rows: 6, cols: 16 },
  dk75: { rows: 6, cols: 15 },
  dk75Pro: { rows: 6, cols: 15 },
  dk75E: { rows: 6, cols: 15 },
  dk87: { rows: 6, cols: 17 },
  dk75Single: { rows: 6, cols: 17 },
  M98: { rows: 6, cols: 18 },
  "75ProTFT": { rows: 6, cols: 15 },
  huafenda98: { rows: 6, cols: 19 },
  M98TFT: { rows: 6, cols: 18 },
  DK108: { rows: 6, cols: 21 },
  "M98 8K": { rows: 6, cols: 18 },
};

/** 贝盈 2.4G 单次 HID 较慢；并发过高易卡死通道，2 路较稳 */
const ADVANCED_KEY_LOAD_CONCURRENCY = 2;
/** 单格 getHigherKey 超时，避免 Promise.all 永久挂起导致全页 Loading 20s 踢回首页 */
const ADVANCED_KEY_CELL_TIMEOUT_MS = 6_000;
/** 全表扫描总时长上限（应小于子应用 Loading maxDuration 20s） */
const ADVANCED_KEY_SCAN_BUDGET_MS = 18_000;

function withAdvancedKeyCellTimeout<T>(
  promise: Promise<T>,
  ms: number = ADVANCED_KEY_CELL_TIMEOUT_MS
): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => {
      setTimeout(() => resolve(undefined), ms);
    }),
  ]);
}

function parseHigherKeyApiResponse(
  response: {
    code?: number;
    data?: { mode?: HigherKeyMode; data?: unknown } | null;
  } | null
): AdvancedKeyConfig | null {
  if (
    !response ||
    response.code !== 200 ||
    !response.data ||
    response.data.mode === HigherKeyMode.NONE ||
    response.data.mode == null
  ) {
    return null;
  }

  const { mode, data } = response.data;

  switch (mode) {
    case HigherKeyMode.DKS:
      return { mode: HigherKeyMode.DKS, data: data as DKSData };
    case HigherKeyMode.MPT:
      return { mode: HigherKeyMode.MPT, data: data as MPTData };
    case HigherKeyMode.MT:
      return { mode: HigherKeyMode.MT, data: data as MTData };
    case HigherKeyMode.TGL:
      return { mode: HigherKeyMode.TGL, data: data as TGLData };
    case HigherKeyMode.END:
      return { mode: HigherKeyMode.END, data: data as ENDData };
    case HigherKeyMode.SOCD:
      return { mode: HigherKeyMode.SOCD, data: data as SOCDData };
    case HigherKeyMode.RS:
      return { mode: HigherKeyMode.RS, data: data as RSData };
    case HigherKeyMode.SNAPKEY:
      return { mode: HigherKeyMode.SNAPKEY, data: data as SnapKeyData };
    case HigherKeyMode.MDS:
      return { mode: HigherKeyMode.MDS, data: data as MDSData };
    case HigherKeyMode.CB:
      return { mode: HigherKeyMode.CB, data: data as CBData };
    default:
      return null;
  }
}

export const useAdvancedKey = ({
  sdkApi,
  keyboardType,
  autoLoad = false,
  solutionProvider,
}: UseAdvancedKeyOptions): UseAdvancedKeyResult => {
  const [advancedKeyCache, setAdvancedKeyCache] = useState<
    Record<string, AdvancedKeyConfig>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [isModifying, setIsModifying] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sdkApiRef = useRef(sdkApi);
  const solutionProviderRef = useRef(solutionProvider);
  const hasLoadedRef = useRef(false);
  const loadGenerationRef = useRef(0);
  const advancedKeyCacheRef = useRef(advancedKeyCache);

  useEffect(() => {
    advancedKeyCacheRef.current = advancedKeyCache;
  }, [advancedKeyCache]);

  useEffect(() => {
    sdkApiRef.current = sdkApi;
  }, [sdkApi]);

  useEffect(() => {
    solutionProviderRef.current = solutionProvider;
  }, [solutionProvider]);

  // 生成缓存键
  const getCacheKey = useCallback((row: number, col: number): string => {
    return `${row}-${col}`;
  }, []);

  // 获取单个高级键配置
  const getHigherKey = useCallback(
    async (row: number, col: number): Promise<AdvancedKeyConfig | null> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) {
        console.error("SDK API 未初始化");
        return null;
      }

      try {
        const response = await api.getHigherKey({ row, col });

        const parsed = parseHigherKeyApiResponse(response);
        const cacheKey = getCacheKey(row, col);
        if (!parsed) {
          setAdvancedKeyCache((prev) => {
            if (!(cacheKey in prev)) return prev;
            const next = { ...prev };
            delete next[cacheKey];
            return next;
          });
          return { mode: HigherKeyMode.NONE };
        }

        setAdvancedKeyCache((prev) => ({
          ...prev,
          [cacheKey]: parsed,
        }));
        return parsed;
      } catch (err) {
        console.error(`获取高级键失败 (${row}, ${col}):`, err);
        return null;
      }
    },
    [getCacheKey]
  );

  const setCB = useCallback(
    async (row: number, col: number, data: CBData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi as any;
      if (!api?.setHigherKeyCB) return false;

      setIsModifying(true);
      try {
        const response = await api.setHigherKeyCB({
          row,
          col,
          mode: "CB",
          data,
        });

        // 与 deleteHigherKey 等一致：以 code=200 为准；部分实现仅返回 { code, message } 无 data
        if (Number(response?.code) === 200) {
          const cacheKey = getCacheKey(row, col);
          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey]: { mode: HigherKeyMode.CB, data },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 CB 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 删除高级键
  const deleteHigherKey = useCallback(
    async (row: number, col: number): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) {
        console.error("SDK API 未初始化");
        return false;
      }

      setIsModifying(true);
      try {
        const response = await api.setHigherKeyNONE({
          row,
          col,
          mode: HigherKeyMode.NONE,
          data: { mode: 0 },
        });

        if (response.code === 200) {
          const cacheKey = getCacheKey(row, col);
          setAdvancedKeyCache((prev) => {
            const newCache = { ...prev };
            delete newCache[cacheKey];
            return newCache;
          });
          return true;
        }

        return false;
      } catch (err) {
        console.error("删除高级键失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 设置 DKS
  const setDKS = useCallback(
    async (row: number, col: number, data: DKSData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) return false;

      setIsModifying(true);
      try {
        const response = await api.setHigherKeyDKS({ row, col, data });

        if (response.code === 200 && response.data) {
          const cacheKey = getCacheKey(row, col);
          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey]: { mode: HigherKeyMode.DKS, data: response.data!.data },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 DKS 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 设置 MPT
  const setMPT = useCallback(
    async (row: number, col: number, data: MPTData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) return false;

      setIsModifying(true);
      try {
        // 转换单位：mm -> 0.001mm
        const convertedData = {
          ...data,
          // dbs: data.dbs.map((db) => Math.round(db * 1000)),
          dbs: data.dbs,
        };

        const response = await api.setHigherKeyMPT({
          row,
          col,
          data: convertedData,
        });

        if (response.code === 200 && response.data) {
          const cacheKey = getCacheKey(row, col);
          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey]: { mode: HigherKeyMode.MPT, data: response.data!.data },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 MPT 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 设置 MT
  const setMT = useCallback(
    async (row: number, col: number, data: MTData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) return false;

      setIsModifying(true);
      try {
        const response = await api.setHigherKeyMT({
          row,
          col,
          mode: "MT",
          data,
        });

        if (response.code === 200 && response.data) {
          const cacheKey = getCacheKey(row, col);
          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey]: { mode: HigherKeyMode.MT, data: response.data!.data },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 MT 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 设置 TGL
  const setTGL = useCallback(
    async (row: number, col: number, data: TGLData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) return false;

      setIsModifying(true);
      try {
        const response = await api.setHigherKeyTGL({
          row,
          col,
          mode: "TGL",
          data,
        });

        if (response.code === 200 && response.data) {
          const cacheKey = getCacheKey(row, col);
          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey]: { mode: HigherKeyMode.TGL, data: response.data!.data },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 TGL 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 设置 END
  const setEND = useCallback(
    async (row: number, col: number, data: ENDData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) return false;

      setIsModifying(true);
      try {
        const response = await api.setHigherKeyEND({
          row,
          col,
          mode: "END",
          data,
        });

        if (response.code === 200 && response.data) {
          const cacheKey = getCacheKey(row, col);
          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey]: { mode: HigherKeyMode.END, data: response.data!.data },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 END 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 设置 SOCD
  const setSOCD = useCallback(
    async (
      row: number,
      col: number,
      row2: number,
      col2: number,
      socdMode: number,
      delay: number,
      kcs: number[]
    ): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) return false;

      setIsModifying(true);
      try {
        // ✅ 按照文档：所有参数平铺，不需要 data 包装
        const response = await api.setHigherKeySOCD({
          row,
          col,
          row2,
          col2,
          mode: "SOCD",
          socdMode,
          delay,
          kcs,
        });

        console.log("📥 SOCD SDK 返回:", response);

        if (response.code === 200 && response.data) {
          // 返回值是数组，包含两个按键的配置
          if (Array.isArray(response.data)) {
            response.data.forEach((item: any) => {
              const cacheKey = getCacheKey(item.row, item.col);
              setAdvancedKeyCache((prev) => ({
                ...prev,
                [cacheKey]: { mode: HigherKeyMode.SOCD, data: item.data },
              }));
            });
          }
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 SOCD 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  // 设置 RS (保持不变，文档确认格式正确)
  const setRS = useCallback(
    async (row: number, col: number, data: RSData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api) return false;

      setIsModifying(true);
      try {
        // ✅ 按照文档：RS 需要 data 包装
        const response = await api.setHigherKeyRS({
          row,
          col,
          mode: "RS",
          data,
        });

        console.log("📥 RS SDK 返回:", response);

        if (response.code === 200 && response.data) {
          // RS 返回单个对象，但需要更新两个按键的缓存
          const cacheKey1 = getCacheKey(row, col);
          const cacheKey2 = getCacheKey(data.row2, data.col2);

          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey1]: { mode: HigherKeyMode.RS, data: response.data!.data },
            [cacheKey2]: { mode: HigherKeyMode.RS, data: response.data!.data },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 RS 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  const setSnapKey = useCallback(
    async (row: number, col: number, data: SnapKeyData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api?.setHigherKeySnapKey) return false;

      setIsModifying(true);
      try {
        const response = await api.setHigherKeySnapKey({
          row,
          col,
          mode: "SNAPKEY",
          data,
        });

        if (response.code === 200 && response.data) {
          const cacheKey1 = getCacheKey(row, col);
          const cacheKey2 = getCacheKey(data.row2, data.col2);

          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey1]: {
              mode: HigherKeyMode.SNAPKEY,
              data: response.data!.data,
            },
            [cacheKey2]: {
              mode: HigherKeyMode.SNAPKEY,
              data: response.data!.data,
            },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 SnapKey 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  const setMDS = useCallback(
    async (row: number, col: number, data: MDSData): Promise<boolean> => {
      const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
      if (!api?.setHigherKeyMDS) return false;

      setIsModifying(true);
      try {
        const response = await api.setHigherKeyMDS({
          row,
          col,
          mode: "MDS",
          data,
        });

        if (response.code === 200 && response.data) {
          const cacheKey1 = getCacheKey(row, col);
          const cacheKey2 = getCacheKey(data.row2, data.col2);

          setAdvancedKeyCache((prev) => ({
            ...prev,
            [cacheKey1]: {
              mode: HigherKeyMode.MDS,
              data: response.data!.data,
            },
            [cacheKey2]: {
              mode: HigherKeyMode.MDS,
              data: response.data!.data,
            },
          }));
          return true;
        }
        return false;
      } catch (err) {
        console.error("设置 MDS 失败:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [getCacheKey]
  );

  const loadInFlightRef = useRef(false);
  // 加载所有高级键
  const loadAllAdvancedKeys = useCallback(
    async (options?: {
      force?: boolean;
    }): Promise<Record<string, AdvancedKeyConfig>> => {
      if (isDeviceSessionStandbyRuntime()) {
        return advancedKeyCacheRef.current;
      }
      if (!sdkApiRef.current?.sdkKeyboardAdvancedKeyApi) {
        console.error("SDK API 未初始化");
        return advancedKeyCacheRef.current;
      }
      if (loadInFlightRef.current && options?.force) {
        loadGenerationRef.current += 1;
        loadInFlightRef.current = false;
      }
      if (loadInFlightRef.current) {
        return advancedKeyCacheRef.current;
      }
      loadInFlightRef.current = true;

      const generation = ++loadGenerationRef.current;
      const mergedCache: Record<string, AdvancedKeyConfig> = {
        ...advancedKeyCacheRef.current,
      };
      // 仅首屏全表扫描显示 Loading；重扫/删改后后台刷新，避免虚拟键盘在骨架屏与完整布局间闪烁
      if (!hasLoadedRef.current) {
        setIsLoading(true);
      }
      setError(null);
      const scanDeadline = Date.now() + ADVANCED_KEY_SCAN_BUDGET_MS;

      try {
        const api = sdkApiRef.current?.sdkKeyboardAdvancedKeyApi;
        if (!api) {
          console.error("SDK API 未初始化");
          return mergedCache;
        }

        // 融园高级键在改键 layout 的 4 字节槽内，一次 layout 扫描即可
        if (solutionProviderRef.current === "rongyuan") {
          const loadFromLayout = (
            api as {
              loadHigherKeysFromLayout?: () => Promise<{
                code?: number;
                data?: Array<{
                  row: number;
                  col: number;
                  mode: number;
                  data?: unknown;
                }>;
              }>;
            }
          ).loadHigherKeysFromLayout;
          if (typeof loadFromLayout === "function") {
            const response = await loadFromLayout();
            if (response?.code === 200 && Array.isArray(response.data)) {
              for (const entry of response.data) {
                if (!entry || entry.mode === 0) continue;
                const cacheKey = getCacheKey(entry.row, entry.col);
                const config = {
                  mode: entry.mode as HigherKeyMode,
                  data: entry.data,
                } as AdvancedKeyConfig;
                mergedCache[cacheKey] = config;
                if (loadGenerationRef.current === generation) {
                  setAdvancedKeyCache((prev) => ({
                    ...prev,
                    [cacheKey]: config,
                  }));
                }
              }
            }
          }
          if (loadGenerationRef.current === generation) {
            hasLoadedRef.current = true;
          }
          return mergedCache;
        }

        // 声明 rows 和 cols 变量
        let rows: number;
        let cols: number;

        const matrixType = keyboardType as CommonKeyboardType;

        // 类型保护
        if (matrixType in KEYBOARD_MATRIX_SIZES) {
          // 从 KEYBOARD_MATRIX_SIZES 中获取行和列
          const matrixSize = KEYBOARD_MATRIX_SIZES[matrixType]; // 获取行和列数据
          rows = matrixSize.rows; // 从对象中提取 rows
          cols = matrixSize.cols; // 从对象中提取 cols
        } else {
          console.warn(`Unsupported keyboard type: ${matrixType}`);
          return mergedCache;
        }

        const coords: Array<{ row: number; col: number }> = [];
        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            coords.push({ row, col });
          }
        }

        let cursor = 0;
        const scanOne = async () => {
          while (cursor < coords.length) {
            if (Date.now() >= scanDeadline) {
              console.warn(
                "[useAdvancedKey] 全表扫描达到时长上限，已保留已读缓存并结束 Loading"
              );
              break;
            }
            const idx = cursor++;
            const coord = coords[idx];
            if (!coord) continue;
            const { row, col } = coord;
            try {
              const response = await withAdvancedKeyCellTimeout(
                api.getHigherKey({ row, col })
              );
              if (!response) continue;
              const config = parseHigherKeyApiResponse(response);
              if (!config) continue;
              if (loadGenerationRef.current !== generation) return;
              const cacheKey = getCacheKey(row, col);
              mergedCache[cacheKey] = config;
              setAdvancedKeyCache((prev) => ({
                ...prev,
                [cacheKey]: config,
              }));
            } catch (err) {
              console.warn(`获取高级键失败 (${row}, ${col}):`, err);
            }
          }
        };

        let loadConcurrency = ADVANCED_KEY_LOAD_CONCURRENCY;
        try {
          const eqRaw =
            typeof localStorage !== "undefined"
              ? localStorage.getItem("equipmentData")
              : null;
          if (eqRaw) {
            const eq = JSON.parse(eqRaw);
            if (
              eq?.solutionRovider === "huafenda" &&
              isHuafenda24GKeyboard(eq)
            ) {
              loadConcurrency = 1;
            }
          }
        } catch {
          // ignore
        }

        const workers = Math.min(loadConcurrency, coords.length);
        await Promise.all(Array.from({ length: workers }, () => scanOne()));

        if (loadGenerationRef.current === generation) {
          hasLoadedRef.current = true;
        }
        return mergedCache;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("加载高级键失败");
        setError(error);
        console.error("加载高级键失败:", err);
        return mergedCache;
      } finally {
        loadInFlightRef.current = false;
        if (loadGenerationRef.current === generation) {
          setIsLoading(false);
        }
      }
    },
    [keyboardType, getCacheKey]
  );

  // 清空缓存
  const clearCache = useCallback(() => {
    setAdvancedKeyCache({});
    advancedKeyCacheRef.current = {};
    hasLoadedRef.current = false;
  }, []);

  const replaceAdvancedKeyCache = useCallback(
    (cache: Record<string, AdvancedKeyConfig>) => {
      advancedKeyCacheRef.current = cache;
      setAdvancedKeyCache(cache);
    },
    []
  );

  // 自动加载
  useEffect(() => {
    if (
      autoLoad &&
      sdkApiRef.current &&
      !hasLoadedRef.current &&
      !isDeviceSessionStandbyRuntime()
    ) {
      loadAllAdvancedKeys();
    }
  }, [autoLoad, loadAllAdvancedKeys]);

  /** Owner 同步完成后刷新高级键（不参与协调器探测） */
  useEffect(() => {
    if (typeof window === "undefined" || !autoLoad) return;
    let wasLoading = false;
    const onLoadingChanged = (ev: Event) => {
      const loading = Boolean(
        (ev as CustomEvent<{ loading?: boolean }>).detail?.loading
      );
      if (wasLoading && !loading && !isDeviceSessionStandbyRuntime()) {
        hasLoadedRef.current = false;
        void loadAllAdvancedKeys();
      }
      wasLoading = loading;
    };
    window.addEventListener(
      OWNER_RESYNC_LOADING_CHANGED_EVENT,
      onLoadingChanged
    );
    return () => {
      window.removeEventListener(
        OWNER_RESYNC_LOADING_CHANGED_EVENT,
        onLoadingChanged
      );
    };
  }, [autoLoad, loadAllAdvancedKeys]);

  return {
    advancedKeyCache,
    isLoading,
    isModifying,
    error,
    getHigherKey,
    deleteHigherKey,
    setDKS,
    setMPT,
    setMT,
    setTGL,
    setEND,
    setSOCD,
    setRS,
    setSnapKey,
    setMDS,
    setCB,
    loadAllAdvancedKeys,
    clearCache,
    replaceAdvancedKeyCache,
  };
};
